import React, { useState, useEffect } from 'react';
import {
  Box, Card, Typography, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Paper, TextField,
  IconButton, Chip, Pagination, Stack,
  FormControl, InputLabel, Select, MenuItem, Grid,
  Alert, CircularProgress
} from '@mui/material';
import {
  Search as SearchIcon,
  Clear as ClearIcon,
  History as HistoryIcon
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import Layout from '../components/Layout';
import api from '../services/api';
import { format } from 'date-fns';
import { useAuth } from '../context/AuthContext';

function AuditLogs() {
  const { user } = useAuth();
  
  // TODOS os Hooks DEVEM vir antes de qualquer return condicional
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    acao: '',
    usuario: '',
    os_id: '',
    data_inicio: '',
    data_fim: ''
  });
  const [pagination, setPagination] = useState({
    page: 1,
    total: 0,
    perPage: 20
  });

  const acoes = [
    { value: 'criar', label: 'Criação', color: 'success' },
    { value: 'editar', label: 'Edição', color: 'primary' },
    { value: 'finalizar', label: 'Finalização', color: 'warning' },
    { value: 'reabrir', label: 'Reabertura', color: 'info' },
    { value: 'excluir', label: 'Exclusão', color: 'error' }
  ];

  // Verificação de permissão DEPOIS dos Hooks
  const isSuperAdmin = user?.tipo === 'super_admin';

  useEffect(() => {
    if (isSuperAdmin) {
      carregarLogs();
    }
  }, [pagination.page, isSuperAdmin]);

  const carregarLogs = async () => {
    setLoading(true);
    try {
      const params = {
        page: pagination.page,
        limit: pagination.perPage,
        ...filters
      };
      
      Object.keys(params).forEach(key => {
        if (params[key] === '' || params[key] === null || params[key] === undefined) {
          delete params[key];
        }
      });

      const response = await api.get('/logs', { params });
      setLogs(response.data.logs || []);
      setPagination(prev => ({ 
        ...prev, 
        total: response.data.total || 0 
      }));
    } catch (error) {
      console.error('Erro ao carregar logs:', error);
      toast.error('Erro ao carregar logs de auditoria');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (e) => {
    setFilters({ ...filters, [e.target.name]: e.target.value });
  };

  const handleSearch = () => {
    setPagination(prev => ({ ...prev, page: 1 }));
    carregarLogs();
  };

  const handleClearFilters = () => {
    setFilters({
      acao: '',
      usuario: '',
      os_id: '',
      data_inicio: '',
      data_fim: ''
    });
    setPagination(prev => ({ ...prev, page: 1 }));
    setTimeout(carregarLogs, 0);
  };

  const handlePageChange = (event, value) => {
    setPagination(prev => ({ ...prev, page: value }));
  };

  const getAcaoColor = (acao) => {
    const found = acoes.find(a => a.value === acao);
    return found ? found.color : 'default';
  };

  const getAcaoLabel = (acao) => {
    const found = acoes.find(a => a.value === acao);
    return found ? found.label : acao;
  };

  // AGORA podemos fazer o return condicional
  if (!isSuperAdmin) {
    return (
      <Layout title="Acesso Negado">
        <Box sx={{ p: 3 }}>
          <Alert severity="error" sx={{ borderRadius: 2 }}>
            Você não tem permissão para acessar esta página.
          </Alert>
        </Box>
      </Layout>
    );
  }

  return (
    <Layout title="Auditoria de Logs">
      <Card sx={{ p: 3, borderRadius: 3 }}>
        {/* Cabeçalho */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
          <Box sx={{ 
            backgroundColor: '#0A2647', 
            borderRadius: 2, 
            p: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <HistoryIcon sx={{ fontSize: 28, color: '#39FF14' }} />
          </Box>
          <Typography variant="h5" sx={{ color: '#0A2647', fontWeight: 600 }}>
            Registro de Auditoria
          </Typography>
        </Box>

        {/* Filtros */}
        <Paper sx={{ p: 2, mb: 3, backgroundColor: '#f8f9fa', borderRadius: 2 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Ação</InputLabel>
                <Select
                  name="acao"
                  value={filters.acao}
                  label="Ação"
                  onChange={handleFilterChange}
                >
                  <MenuItem value="">Todas</MenuItem>
                  {acoes.map(a => (
                    <MenuItem key={a.value} value={a.value}>
                      {a.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                size="small"
                label="Usuário"
                name="usuario"
                value={filters.usuario}
                onChange={handleFilterChange}
                placeholder="Nome ou email"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                fullWidth
                size="small"
                label="OS ID"
                name="os_id"
                value={filters.os_id}
                onChange={handleFilterChange}
                type="number"
                placeholder="Número da OS"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                fullWidth
                size="small"
                label="Data Início"
                name="data_inicio"
                type="date"
                value={filters.data_inicio}
                onChange={handleFilterChange}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                fullWidth
                size="small"
                label="Data Fim"
                name="data_fim"
                type="date"
                value={filters.data_fim}
                onChange={handleFilterChange}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={12} md={1}>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <IconButton 
                  onClick={handleSearch}
                  sx={{ 
                    backgroundColor: '#0A2647', 
                    color: '#39FF14',
                    '&:hover': { backgroundColor: '#1a3a5f' }
                  }}
                >
                  <SearchIcon />
                </IconButton>
                <IconButton 
                  onClick={handleClearFilters}
                  sx={{ border: '1px solid #ccc' }}
                >
                  <ClearIcon />
                </IconButton>
              </Box>
            </Grid>
          </Grid>
        </Paper>

        {/* Tabela */}
        <TableContainer component={Paper} sx={{ maxHeight: 500, borderRadius: 2 }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f0f0f0' }}>Data/Hora</TableCell>
                <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f0f0f0' }}>Usuário</TableCell>
                <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f0f0f0' }}>Ação</TableCell>
                <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f0f0f0' }}>OS</TableCell>
                <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f0f0f0' }}>Detalhes</TableCell>
                <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f0f0f0' }}>IP</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 5 }}>
                    <CircularProgress />
                    <Typography sx={{ mt: 2, color: '#666' }}>Carregando logs...</Typography>
                  </TableCell>
                </TableRow>
              ) : logs.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 5 }}>
                    <Alert severity="info" sx={{ display: 'inline-block', borderRadius: 2 }}>
                      Nenhum registro encontrado
                    </Alert>
                  </TableCell>
                </TableRow>
              ) : (
                logs.map((log) => (
                  <TableRow key={log.id} hover>
                    <TableCell>
                      {format(new Date(log.created_at), 'dd/MM/yyyy HH:mm:ss')}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {log.usuario_nome}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        {log.usuario_email}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={getAcaoLabel(log.acao)}
                        size="small"
                        color={getAcaoColor(log.acao)}
                        variant="filled"
                      />
                    </TableCell>
                    <TableCell>
                      {log.os_id ? (
                        <Chip 
                          label={`#${log.os_id}`} 
                          size="small" 
                          variant="outlined"
                          onClick={() => window.open(`/os/${log.os_id}`, '_blank')}
                          sx={{ 
                            cursor: 'pointer',
                            '&:hover': { backgroundColor: '#e3f2fd' }
                          }}
                        />
                      ) : '-'}
                    </TableCell>
                    <TableCell>
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          maxWidth: 300, 
                          whiteSpace: 'nowrap', 
                          overflow: 'hidden', 
                          textOverflow: 'ellipsis' 
                        }}
                        title={log.detalhes}
                      >
                        {log.detalhes || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" fontFamily="monospace">
                        {log.ip || '-'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Paginação */}
        {pagination.total > 0 && (
          <Stack spacing={2} sx={{ mt: 3, alignItems: 'center' }}>
            <Pagination
              count={Math.ceil(pagination.total / pagination.perPage)}
              page={pagination.page}
              onChange={handlePageChange}
              color="primary"
              size="large"
              showFirstButton
              showLastButton
              sx={{
                '& .MuiPaginationItem-root': {
                  color: '#0A2647',
                }
              }}
            />
            <Typography variant="caption" color="textSecondary">
              Total de {pagination.total} registro(s)
            </Typography>
          </Stack>
        )}
      </Card>
    </Layout>
  );
}

export default AuditLogs;
